import { putFile, unwrap, unwrapBlob, withSignal } from "@nexxt/api-client";
import { filterQuery } from "@nexxt/app-kit";
import type { CatalogClient } from "@nexxt/catalog-ui";
import { PRODUCT_IMAGES_FOLDER } from "@nexxt/contracts/catalog";
import { api } from "./api";

// The catalog screens on gateway-atelier (ADR-166, ADR-167): one call per operation, input and answer of its contract.
// The gateway has the brand's operations alone (ExternalCatalogService), and the upload of a file (ADR-171).

const { catalog } = api;
const byId = catalog.linesheets[":linesheetId"];
const task = catalog["attribute-tasks"][":taskId"];

export const catalogClient: CatalogClient = {
  listLinesheets: (input, signal) => unwrap(catalog.linesheets.$get({ query: filterQuery(input) }, withSignal(signal))),
  getLinesheet: (param, signal) => unwrap(catalog.linesheets[":key"].$get({ param }, withSignal(signal))),
  checkLinesheet: ({ linesheetId, ...query }, signal) =>
    unwrap(byId.check.$get({ param: { linesheetId }, query }, withSignal(signal))),
  getLinesheetHistory: ({ linesheetId, ...query }, signal) =>
    unwrap(byId.history.$get({ param: { linesheetId }, query: filterQuery(query) }, withSignal(signal))),
  getLinesheetOptions: (_, signal) => unwrap(catalog["linesheet-options"].$get({}, withSignal(signal))),
  createLinesheet: (json) => unwrap(catalog.linesheets.$post({ json })),
  updateLinesheet: ({ linesheetId, ...json }) => unwrap(byId.$patch({ param: { linesheetId }, json })),
  submitLinesheet: ({ linesheetId, version }) =>
    unwrap(byId.submit.$post({ param: { linesheetId }, json: { version } })),
  listLinesheetLines: ({ linesheetId, ...query }, signal) =>
    unwrap(byId.lines.$get({ param: { linesheetId }, query: filterQuery(query) }, withSignal(signal))),
  getLineOptions: (_, signal) => unwrap(catalog["line-options"].$get({}, withSignal(signal))),
  saveLinesheetLines: ({ linesheetId, ...json }) => unwrap(byId.lines.save.$post({ param: { linesheetId }, json })),
  removeLinesheetLines: ({ linesheetId, ...json }) => unwrap(byId.lines.remove.$post({ param: { linesheetId }, json })),
  requestLineDeletion: ({ linesheetId, ...json }) =>
    unwrap(byId.lines["request-deletion"].$post({ param: { linesheetId }, json })),
  listCarryoverCandidates: ({ linesheetId, ...query }, signal) =>
    unwrap(
      byId["carryover-candidates"].$get({ param: { linesheetId }, query: filterQuery(query) }, withSignal(signal)),
    ),
  changeLineCarryover: ({ linesheetId, ...json }) =>
    unwrap(byId.lines.carryover.$post({ param: { linesheetId }, json })),
  listAttributeTasks: (input, signal) =>
    unwrap(catalog["attribute-tasks"].$get({ query: filterQuery(input) }, withSignal(signal))),
  listAttributeCompleteness: (input, signal) =>
    unwrap(catalog["attribute-completeness"].$get({ query: filterQuery(input) }, withSignal(signal))),
  getAttributeTask: (param, signal) => unwrap(task.$get({ param }, withSignal(signal))),
  listTaskSkus: ({ taskId, ...query }, signal) =>
    unwrap(task.skus.$get({ param: { taskId }, query: filterQuery(query) }, withSignal(signal))),
  getAttributeOptions: (_, signal) => unwrap(catalog["attribute-options"].$get({}, withSignal(signal))),
  saveSkuAttributes: ({ taskId, ...json }) => unwrap(task.skus.save.$post({ param: { taskId }, json })),
  submitAttributes: ({ taskId, version }) => unwrap(task.submit.$post({ param: { taskId }, json: { version } })),
  importLinesheetLines: (json) => unwrap(catalog.imports["linesheet-lines"].$post({ json })),
  importSkuAttributes: (json) => unwrap(catalog.imports["sku-attributes"].$post({ json })),
  listImports: ({ linesheetId, ...query }, signal) =>
    unwrap(catalog.imports.$get({ query: { linesheetId, ...filterQuery(query) } }, withSignal(signal))),
  listImportErrors: ({ batchId, ...query }, signal) =>
    unwrap(
      catalog.imports[":batchId"].errors.$get({ param: { batchId }, query: filterQuery(query) }, withSignal(signal)),
    ),
  uploadImportFile: (file) => putFile(api.files.files[":fileId"], file, "imports"),
  listProductImages: ({ productId }, signal) =>
    unwrap(catalog.products[":productId"].images.$get({ param: { productId } }, withSignal(signal))),
  addProductImages: ({ productId, ...json }) =>
    unwrap(catalog.products[":productId"].images.$post({ param: { productId }, json })),
  reorderProductImages: ({ productId, ...json }) =>
    unwrap(catalog.products[":productId"].images.order.$put({ param: { productId }, json })),
  removeProductImage: (param) => unwrap(catalog.products[":productId"].images[":imageId"].$delete({ param })),
  downloadProductImage: (param, signal) =>
    unwrapBlob(catalog.products[":productId"].images[":imageId"].content.$get({ param }, withSignal(signal))),
  uploadProductImageFile: (file) => putFile(api.files.files[":fileId"], file, PRODUCT_IMAGES_FOLDER),
};
